export type ReadingBlockType =
  | "text"
  | "image"
  | "text_with_image"
  | "section_header"
  | "separator";

export type ImagePosition =
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "background";

export type ReadingMode = "fixed" | "dynamic";

// Tipo y nivel de lectura para el programa de Comprensión Lectora.
export type ReadingType =
  | "CUENTOS"
  | "CIENTIFICOS"
  | "LIRICOS"
  | "PERIODISTICOS"
  | "DRAMATICOS"
  | "ARGUMENTATIVOS"
  | "EPISTOLARES"
  | "AUTOBIOGRAFICOS"
  | "FABULAS_MITOS";

export type ReadingLevel = "LEVEL_1" | "LEVEL_2" | "LEVEL_3";

// Géneros de lectura (antes "Tipo de lectura"). El valor es el enum de DB
// (columna reading_type) y el label es lo que se muestra en el editor y en los
// filtros de Comprensión Lectora.
export const READING_TYPE_OPTIONS: { value: ReadingType; label: string }[] = [
  { value: "CUENTOS", label: "Cuentos" },
  { value: "CIENTIFICOS", label: "Científicos" },
  { value: "LIRICOS", label: "Líricos" },
  { value: "PERIODISTICOS", label: "Periodísticos" },
  { value: "DRAMATICOS", label: "Dramáticos" },
  { value: "ARGUMENTATIVOS", label: "Argumentativos" },
  { value: "EPISTOLARES", label: "Epistolares" },
  { value: "AUTOBIOGRAFICOS", label: "(Auto)biográficos" },
  { value: "FABULAS_MITOS", label: "Fábulas y mitos" },
];

export const READING_LEVEL_OPTIONS: { value: ReadingLevel; label: string }[] = [
  { value: "LEVEL_1", label: "Nivel 1" },
  { value: "LEVEL_2", label: "Nivel 2" },
  { value: "LEVEL_3", label: "Nivel 3" },
];

export interface ReadingBlock {
  id: number;
  order: number;
  type: ReadingBlockType;
  content?: string | null;
  image_url?: string | null;
  image_position?: ImagePosition | null;
  section_title?: string | null;
  page_number?: number | null;
}

export interface ReadingContentMetadata {
  author?: string | null;
  illustrator?: string | null;
  genre?: string | null;
  estimated_reading_time_minutes?: number | null;
  reading_type?: ReadingType | null;
  level?: ReadingLevel | null;
  /**
   * @deprecated Legacy single-activity HTML string, kept transitionally by the
   * backend for one release. The BO reads `ReadingContent.activities[]`
   * instead; only used here as a hydration fallback for old readings.
   */
  activity?: string | null;
}

/** A single ordered activity block, as returned by the reading-content API. */
export interface ReadingActivity {
  id: number;
  title: string | null;
  content: string;
  order: number;
}

/** Activity block payload accepted by create/update reading-content requests. */
export interface ReadingActivityDto {
  id?: number;
  title?: string;
  content: string;
  order: number;
}

export interface ReadingContent {
  id: number;
  resource_id: number;
  mode: ReadingMode;
  title: string;
  cover_url?: string | null;
  blocks: ReadingBlock[];
  /** Ordered activity blocks. Source of truth for the "Actividad de lectura" tab. */
  activities?: ReadingActivity[];
  /**
   * Créditos extendidos (licencia/atribución) para lecturas adaptadas de
   * StoryWeaver/BookDash. El backend lo expone a nivel raíz (no en metadata).
   */
  extended_credits?: string | null;
  metadata: ReadingContentMetadata;
}

// DTOs for create/update

export interface CreateReadingBlockDto {
  order_by: number;
  type: ReadingBlockType;
  content?: string;
  image_url?: string;
  image_position?: ImagePosition;
  section_title?: string;
  page_number?: number;
}

export interface CreateReadingContentDto {
  resource_id: number;
  title: string;
  cover_url?: string;
  mode: ReadingMode;
  author?: string;
  illustrator?: string;
  genre?: string;
  estimated_reading_time_minutes?: number;
  reading_type?: ReadingType;
  level?: ReadingLevel;
  extended_credits?: string | null;
  activities?: ReadingActivityDto[];
  blocks?: CreateReadingBlockDto[];
}

export interface UpdateReadingContentDto {
  title?: string;
  cover_url?: string;
  mode?: ReadingMode;
  author?: string;
  illustrator?: string;
  genre?: string;
  estimated_reading_time_minutes?: number;
  reading_type?: ReadingType;
  level?: ReadingLevel;
  /** `null` limpia los créditos guardados; `undefined` no toca la columna. */
  extended_credits?: string | null;
  activities?: ReadingActivityDto[];
}

export interface UpdateReadingBlockDto {
  order_by?: number;
  type?: ReadingBlockType;
  content?: string;
  image_url?: string;
  image_position?: ImagePosition;
  section_title?: string;
  page_number?: number;
}

export interface ReorderBlocksDto {
  block_ids: number[];
}

// Draft types for inline editor

export interface LocalBlock {
  id?: number;
  tempId: string;
  type: ReadingBlockType;
  content?: string;
  imageUrl?: string;
  imageFile?: File;
  order?: number;
  /** Passthrough-only for section_header; the editor has no UI to create/edit it. */
  sectionTitle?: string;
  /** Passthrough-only for text_with_image; the editor has no UI to create/edit it. */
  imagePosition?: ImagePosition;
}

export interface PageGroup {
  pageNumber: number;
  blocks: LocalBlock[];
}

/** Local editor state for one activity block in the "Actividad de lectura" tab. */
export interface ReadingActivityDraft {
  id?: number;
  tempId: string;
  title?: string;
  content: string;
  order: number;
}

export interface ReadingContentDraft {
  title: string;
  mode: ReadingMode;
  coverUrl?: string;
  coverFile?: File;
  author?: string;
  illustrator?: string;
  genre?: string;
  estimatedReadingTimeMinutes?: number;
  readingType?: ReadingType;
  level?: ReadingLevel;
  /** Créditos extendidos (solo lecturas adaptadas de StoryWeaver/BookDash). */
  extendedCredits?: string;
  activities: ReadingActivityDraft[];
  pages: PageGroup[];
}
